import React from 'react';
import { Play, ChevronRight } from 'lucide-react';
import { getRedGifsThumbnail } from '@/lib/utils';
import { COLORS } from '@/data';
import type { Clip } from '@/types';

interface ClipCardProps {
  clip: Clip;
  onClick: () => void;
  selected?: boolean;
  variant?: 'list' | 'grid' | 'compact';
  showArrow?: boolean;
}

export const ClipCard: React.FC<ClipCardProps> = ({
  clip,
  onClick,
  selected = false,
  variant = 'list',
  showArrow = true,
}) => {
  const thumbnail = getRedGifsThumbnail(clip.link);

  // Compact grid variant (for "More in Category")
  if (variant === 'compact') {
    return (
      <button
        onClick={onClick}
        className={`rounded-lg overflow-hidden transition-all ${
          selected
            ? 'ring-2 ring-white'
            : 'hover:ring-2 hover:ring-zinc-500'
        }`}
      >
        <div className="aspect-video bg-zinc-900 relative">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={clip.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-6 h-6" style={{ color: COLORS.lime }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-2">
            <p className="font-medium text-white text-xs truncate">{clip.title}</p>
          </div>
        </div>
      </button>
    );
  }

  // Grid variant
  if (variant === 'grid') {
    return (
      <button
        onClick={onClick}
        className={`rounded-xl overflow-hidden transition-all ${
          selected
            ? 'ring-2 ring-white'
            : 'hover:ring-2 hover:ring-zinc-600'
        }`}
      >
        <div className="aspect-video bg-zinc-900 relative">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={clip.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-8 h-8" style={{ color: COLORS.lime }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="font-medium text-white text-sm truncate">{clip.title}</p>
            {clip.description && (
              <p className="text-zinc-400 text-xs truncate mt-1">{clip.description}</p>
            )}
          </div>
        </div>
      </button>
    );
  }

  // List variant (default)
  return (
    <button
      onClick={onClick}
      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 hover:border-zinc-600 transition-colors text-left"
    >
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0 relative">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={clip.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`absolute inset-0 flex items-center justify-center ${thumbnail ? 'hidden' : ''}`}>
            <Play className="w-6 h-6" style={{ color: COLORS.lime }} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-medium truncate">{clip.title}</div>
          {clip.description && (
            <div className="text-zinc-500 text-sm line-clamp-2">{clip.description}</div>
          )}
        </div>
        {showArrow && <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0" />}
      </div>
    </button>
  );
};
